// ✕ del regalo en el resumen: quita la promo de ese regalo por la fila de la sesión; si venía del 04
// vuelve a «Regalos» como pendiente. No suelta la línea GIFT a mano: la suelta Promociones.
import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { AnchorGiftLine } from '@/domains/sales/wizard/promotionSlots';
import type { AnchorSlotContext } from '@/domains/sales/wizard/steps/BuildSaleStep';
import { evaluationKey } from '../api/useWizardPromotions';
import { wizardPromotionsClient } from '../api/wizardPromotions.client';
import { applyChange, type PromotionsApi, type SelectionChange } from '../lib/applySelection';
import { withGiftRemoved } from '../lib/pendingGifts';
import { needsSessionRefresh, promotionErrorCopy } from '../lib/promotionErrorCopy';
import { rememberedPromotionName } from '../lib/promotionNames';
import {
    recordDiscountCodeBlock,
    storePort,
    trackPendingChange,
} from '../store/promotionSelectionStore';
import { finishWrite, startWrite } from './settleAfterWrites';

export interface RemoveGiftDeps {
    sessionId: string;
    queryClient?: QueryClient;
    api?: PromotionsApi;
}

export type RemoveGift = (gift: AnchorGiftLine, context: AnchorSlotContext) => Promise<void>;

export function createRemoveGift({
    sessionId,
    queryClient,
    api = wizardPromotionsClient,
}: RemoveGiftDeps): RemoveGift {
    return async (gift, context) => {
        const { promotionId } = gift;
        if (!promotionId || context.quoteState === 'charged') return;
        if (!(await context.confirmQuoteDiscard())) return;
        const change: SelectionChange = (base) =>
            withGiftRemoved(
                base,
                base.anchorLineId ?? context.anchorLine.lineId,
                promotionId,
                gift.sku,
            );
        startWrite(sessionId);
        trackPendingChange(sessionId, change, true);
        let changed = true;
        try {
            await applyChange(sessionId, change, storePort(sessionId), api);
        } catch (error) {
            changed = needsSessionRefresh(error);
            recordDiscountCodeBlock(sessionId, error);
            toast.error(promotionErrorCopy(error, (id) => rememberedPromotionName(sessionId, id)));
        } finally {
            trackPendingChange(sessionId, change, false);
            finishWrite(sessionId, changed, () => {
                void queryClient?.invalidateQueries({ queryKey: evaluationKey(sessionId) });
                void context.refreshSession();
            });
        }
    };
}
